/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useLogProfilesList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing Log Profiles list data and DataGrid state.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { fetchLogProfiles } from '../api/logProfilesApi';
import { useLogProfilesColumns } from './useLogProfilesColumns';

export function useLogProfilesList() {
  const { user } = useAuthStore();
  const { showError } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug || t('logProfiles.defaultTenant');
  const availableColumns = useLogProfilesColumns();

  const fetchLogProfilesData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const response = await fetchLogProfiles(params, tenantSlug, user?.id);
        return {
          data: response.data || [],
          pagination: response.pagination ? {
            page: response.pagination.page,
            limit: response.pagination.limit,
            total: response.pagination.total,
            totalPages: response.pagination.pages
          } : {
            page: params.page,
            limit: params.limit,
            total: 0,
          totalPages: 0
        }
      } as DataGridFetchResult;
    } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('logProfiles.errors.loadFailed', 'Failed to load log profiles');
        showError(message);
        throw error;
      }
    },
    [tenantSlug, user?.id, showError, t],
  );

  return useDataGrid({
    fetchData: fetchLogProfilesData,
    initialColumns: availableColumns,
    initialSortBy: 'created_at',
    initialSortOrder: 'desc',
    initialLimit: 10,
    moduleName: 'logProfiles',
  });
}

